import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Scale, Bookmark, Loader2, Globe, Database, Landmark, CheckCircle2, Info, X, RotateCcw } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { useAuth } from "@/contexts/AuthContext";
import {
    searchJudicial,
    fetchCities,
    fetchSpecialties,
    watchProceso,
    type SearchParams,
    fetchPublicacionesFilters,
    watchPublicacionesOffice,
    type HierarchicalFilter,
    type JudicialSearchResult,
    fetchUltimasActuacionesPublicaciones,
    type PublicacionActuacion,
    getSignedPublicacionesProxyUrl,
    fetchSamaiSearchFilters,
    type SamaiSearchFilters,
} from "@/services/scraperApi";
import { getVigenteStatusInfo } from "@/lib/processStatus";

const SOURCES = [
    { id: "cpnu", label: "CPNU", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    { id: "siugj", label: "SIUGJ", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { id: "samai", label: "SAMAI", color: "bg-amber-100 text-amber-700 border-amber-200" },
    { id: "tyba", label: "TYBA", color: "bg-rose-100 text-rose-700 border-rose-200" },
];

const DESPACHOS_DEFAULT_LIMITS = {
    consultas: 50,
    seguimientos: 50,
} as const;

export const JudicialSearchPage = ({ samaiOnly = false }: { samaiOnly?: boolean }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { checkFeatureUsage, getFeatureUsage, refreshStats } = useUsageLimits();
    const CONSULTA_FEATURE = "despachos_consultas";
    const SEGUIMIENTO_FEATURE = "despachos_seguimientos";

    // Search state
    const [query, setQuery] = useState("");
    const [searchType, setSearchType] = useState<SearchParams["type"]>("nombre");
    const [selectedSources, setSelectedSources] = useState<string[]>(samaiOnly ? ["samai"] : ["cpnu", "siugj", "samai", "tyba"]);
    const [tipoSujeto, setTipoSujeto] = useState("0002");
    const [tipoPersona, setTipoPersona] = useState("1");
    const [cityName, setCityName] = useState("all");
    const [specialty, setSpecialty] = useState("all");
    const [samaiFilters, setSamaiFilters] = useState<SamaiSearchFilters>({});
    const [samaiSearchMode, setSamaiSearchMode] = useState("radicado");
    const [samaiAreaMode, setSamaiAreaMode] = useState("");
    const [samaiEstado, setSamaiEstado] = useState("");
    const [samaiParte, setSamaiParte] = useState("");
    const [samaiExactPhrase, setSamaiExactPhrase] = useState(false);
    const [samaiCorporacion, setSamaiCorporacion] = useState("");
    const [samaiSeccion, setSamaiSeccion] = useState("");
    const [samaiPonente, setSamaiPonente] = useState("");

    // Data
    const [cities, setCities] = useState<string[]>([]);
    const [specialties, setSpecialties] = useState<string[]>([]);
    const [results, setResults] = useState<JudicialSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [watchingIds, setWatchingIds] = useState<Set<string>>(new Set());

    // Publicaciones Procesales state
    const [activeTab, setActiveTab] = useState<"search" | "publicaciones">("search");
    const [hFilters, setHFilters] = useState({
        deptos: [] as HierarchicalFilter[],
        munis: [] as HierarchicalFilter[],
        entities: [] as HierarchicalFilter[],
        specialties: [] as HierarchicalFilter[],
        offices: [] as HierarchicalFilter[],
    });
    const [hSelected, setHSelected] = useState({
        depto: "",
        muni: "",
        entidad: "",
        esp: "",
        despacho: "",
    });
    const [radicadoFilter, setRadicadoFilter] = useState("");
    const [isWatchingOffice, setIsWatchingOffice] = useState(false);
    const [ppResults, setPpResults] = useState<PublicacionActuacion[]>([]);
    const [isHistorical, setIsHistorical] = useState(false);
    const [cpnuCard, setCpnuCard] = useState<JudicialSearchResult | null>(null);
    const [isLoadingActuaciones, setIsLoadingActuaciones] = useState(false);
    const [isLoadingOffices, setIsLoadingOffices] = useState(false);
    const [officeLoadMessage, setOfficeLoadMessage] = useState("");
    const consultaStats = getFeatureUsage(CONSULTA_FEATURE);
    const seguimientoStats = getFeatureUsage(SEGUIMIENTO_FEATURE);
    const consultaUsed = consultaStats?.current_usage || 0;
    const consultaLimit = consultaStats?.daily_limit || DESPACHOS_DEFAULT_LIMITS.consultas;
    const consultaRemaining = Math.max(consultaLimit - consultaUsed, 0);
    const seguimientoUsed = seguimientoStats?.current_usage || 0;
    const seguimientoLimit = seguimientoStats?.daily_limit || DESPACHOS_DEFAULT_LIMITS.seguimientos;
    const seguimientoRemaining = Math.max(seguimientoLimit - seguimientoUsed, 0);

    // Load cities on mount
    useEffect(() => {
        fetchCities().then(setCities).catch(() => setCities([]));
    }, []);

    // Load specialties when city changes
    useEffect(() => {
        if (cityName && cityName !== "all") {
            fetchSpecialties(cityName).then(setSpecialties).catch(() => setSpecialties([]));
        } else {
            setSpecialties([]);
            setSpecialty("");
        }
    }, [cityName]);

    useEffect(() => {
        if (!selectedSources.includes("samai")) return;
        setSearchType("radicado");
        fetchSamaiSearchFilters()
            .then((data) => {
                setSamaiFilters(data || {});
                const defaultMode = (data?.search_modes || []).find((m: any) => m.checked)?.id || "radicado";
                setSamaiSearchMode(defaultMode);
            })
            .catch(() => setSamaiFilters({}));
    }, [selectedSources]);

    useEffect(() => {
        if (!selectedSources.includes("samai")) return;
        fetchSamaiSearchFilters({ corporacion: samaiCorporacion })
            .then((data) => setSamaiFilters(prev => ({ ...prev, seccion_options: data.seccion_options || prev.seccion_options })))
            .catch(() => undefined);
        setSamaiSeccion("");
        setSamaiPonente("");
    }, [samaiCorporacion, selectedSources]);

    useEffect(() => {
        if (!selectedSources.includes("samai") || !samaiSeccion) return;
        fetchSamaiSearchFilters({ corporacion: samaiCorporacion, seccion: samaiSeccion })
            .then((data) => setSamaiFilters(prev => ({ ...prev, ponente_options: data.ponente_options || prev.ponente_options })))
            .catch(() => undefined);
        setSamaiPonente("");
    }, [samaiSeccion, samaiCorporacion, selectedSources]);

    // Publicaciones Cascade
    // Departments, Entities, and Specialties load when tab activates
    useEffect(() => {
        if (activeTab === "publicaciones") {
            fetchPublicacionesFilters("department")
                .then(d => setHFilters(prev => ({ ...prev, deptos: d })))
                .catch((err) => {
                    setHFilters(prev => ({ ...prev, deptos: [] }));
                    toast({
                        title: "No se pudieron cargar departamentos",
                        description: String(err),
                        variant: "destructive"
                    });
                });
            // Entities and Specialties are global lists (same across all municipalities)
            fetchPublicacionesFilters("entity")
                .then(d => setHFilters(prev => ({ ...prev, entities: d })))
                .catch(() => setHFilters(prev => ({ ...prev, entities: [] })));
            // Specialty is dependent on selected municipality + entity.
            setHFilters(prev => ({ ...prev, specialties: [] }));
        }
    }, [activeTab, toast]);

    // Municipalities cascade from Department
    useEffect(() => {
        if (hSelected.depto) {
            fetchPublicacionesFilters("municipality", hSelected.depto)
                .then(d => setHFilters(prev => ({ ...prev, munis: d })))
                .catch(() => setHFilters(prev => ({ ...prev, munis: [] })));
            setHSelected(prev => ({ ...prev, muni: "", despacho: "" }));
            setHFilters(prev => ({ ...prev, offices: [] }));
        }
    }, [hSelected.depto]);

    // Specialties cascade from municipality + entity
    useEffect(() => {
        setHSelected(prev => ({ ...prev, esp: "", despacho: "" }));
        setHFilters(prev => ({ ...prev, specialties: [], offices: [] }));

        if (hSelected.muni && hSelected.entidad) {
            fetchPublicacionesFilters("specialty", undefined, {
                muni_id: hSelected.muni,
                entidad_id: hSelected.entidad,
            })
                .then(d => {
                    setHFilters(prev => ({ ...prev, specialties: d }));
                    if (!d.length) {
                        setOfficeLoadMessage("No hay especialidades para este municipio/entidad.");
                    } else {
                        setOfficeLoadMessage("");
                    }
                })
                .catch((err) => {
                    setHFilters(prev => ({ ...prev, specialties: [] }));
                    toast({
                        title: "Error cargando especialidades",
                        description: String(err),
                        variant: "destructive"
                    });
                });
        }
    }, [hSelected.muni, hSelected.entidad, toast]);

    // Offices: reset and re-fetch when muni/entity/specialty change
    useEffect(() => {
        // Always reset office selection when filters change
        setHSelected(prev => ({ ...prev, despacho: "" }));
        setHFilters(prev => ({ ...prev, offices: [] }));

        // Fetch offices only when all 3 filters are selected
        if (hSelected.muni && hSelected.entidad && hSelected.esp) {
            setIsLoadingOffices(true);
            setOfficeLoadMessage("");
            fetchPublicacionesFilters("office", undefined, {
                muni_id: hSelected.muni,
                entidad_id: hSelected.entidad,
                esp_id: hSelected.esp,
            })
                .then(d => {
                    setHFilters(prev => ({ ...prev, offices: d }));
                    if (!d.length) {
                        setOfficeLoadMessage("No se encontraron despachos para esta combinación de filtros.");
                    }
                })
                .catch((err) => {
                    setHFilters(prev => ({ ...prev, offices: [] }));
                    setOfficeLoadMessage("Error cargando despachos.");
                    toast({
                        title: "Error cargando despachos",
                        description: String(err),
                        variant: "destructive",
                    });
                })
                .finally(() => setIsLoadingOffices(false));
        }
    }, [hSelected.muni, hSelected.entidad, hSelected.esp, toast]);

    useEffect(() => {
        refreshStats();
    }, [refreshStats]);

    const toggleSource = (id: string) => {
        if (samaiOnly) {
            if (id === "samai") {
                setSelectedSources(["samai"]);
                setActiveTab("search");
            } else {
                setSelectedSources((prev) => {
                    const filtered = prev.filter((s) => s !== "samai");
                    const hasId = filtered.includes(id);
                    let next = hasId ? filtered.filter((s) => s !== id) : [...filtered, id];
                    if (next.length === 0) {
                        next = [id];
                    }
                    return next;
                });
                setActiveTab("publicaciones");
            }
        } else {
            setSelectedSources((prev) =>
                prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
            );
        }
    };

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();

        const isPP = activeTab === "publicaciones";
        const hasRadicado = isPP && radicadoFilter.trim().length >= 10;
        const ppReady = isPP && (hasRadicado || Boolean(hSelected.despacho));
        const searchReady = !isPP && query.trim() && selectedSources.length > 0;

        if (!searchReady && !ppReady) return;
        const usageCheck = await checkFeatureUsage(CONSULTA_FEATURE, DESPACHOS_DEFAULT_LIMITS.consultas);
        if (!usageCheck.allowed) {
            toast({
                title: "Sin consultas disponibles",
                description: "Alcanzaste tu limite diario de consultas. Vuelve a intentar manana.",
                variant: "destructive",
            });
            return;
        }

        setIsSearching(true);
        setHasSearched(true);
        setCpnuCard(null);
        setPpResults([]);
        try {
            if (isPP) {
                // 1. Lightweight search → process card
                const cardData = await searchJudicial({
                    query: radicadoFilter.trim(),
                    type: "radicado",
                    sources: ["samai", "cpnu", "tyba"],
                });

                if (cardData.results?.length > 0) {
                    setCpnuCard(cardData.results[0]);
                } else {
                    // Create a fallback card using the selected office
                    const selectedOfficeName = hFilters.offices.find(o => o.id === hSelected.despacho)?.name || "Despacho seleccionado";
                    setCpnuCard({
                        radicado: radicadoFilter.trim(),
                        fuente: samaiOnly ? "SAMAI" : "PUBLICACIONES",
                        tipo_proceso: "Proceso a vigilar",
                        juzgado: selectedOfficeName,
                        estado: "Pendiente de vigilancia",
                        url: "",
                        sujetos: []
                    } as any);
                }

                // Show process card right away so user can see details & click "Vigilar" immediately
                setIsSearching(false);
                setResults([]);

                // 2. Fetch live results asynchronously (which runs Publicaciones, CPNU, and Historico)
                setIsLoadingActuaciones(true);
                toast({
                    title: "Consulta inicial en curso",
                    description: "El scrapper está consultando publicaciones y actuaciones. Puedes vigilar el proceso en cualquier momento sin esperar.",
                });

                try {
                    const ppData = await fetchUltimasActuacionesPublicaciones({
                        depto_id: hSelected.depto,
                        muni_id: hSelected.muni,
                        entidad_id: hSelected.entidad,
                        esp_id: hSelected.esp,
                        despacho_id: hSelected.despacho,
                        radicado: radicadoFilter.trim(),
                        historical: isHistorical
                    });

                    if (ppData.results && ppData.results.length > 0) {
                        setPpResults(ppData.results);

                        // Enrich cpnuCard with deep CPNU metadata returned by backend
                        const metaItem = ppData.results.find(r => r.metadata && Object.keys(r.metadata).length > 0);
                        if (metaItem && metaItem.metadata) {
                            const m = metaItem.metadata;
                            setCpnuCard(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    tipo_proceso: m["Tipo de Proceso"] || m["tipo_proceso"] || prev.tipo_proceso,
                                    clase: m["Clase de Proceso"] || m["clase"] || prev.clase,
                                    subclase: m["Subclase de Proceso"] || m["subclase"] || prev.subclase,
                                    ponente: m["Ponente"] || m["juez_ponente"] || prev.ponente,
                                    recurso: m["Recurso"] || m["recurso"] || prev.recurso,
                                    ubicacion_expediente: m["Ubicación del Expediente"] || m["Ubicación"] || m["Despacho"] || prev.ubicacion_expediente,
                                    contenido_radicacion: m["Contenido de Radicación"] || m["contenido_radicacion"] || prev.contenido_radicacion,
                                    fecha_radicado: m["Fecha de Radicación"] || m["fecha_radicacion"] || prev.fecha_radicado,
                                    juzgado: m["Despacho"] || m["Juzgado"] || prev.juzgado,
                                    metadata: {
                                        ...(prev.metadata || {}),
                                        ...m
                                    }
                                };
                            });
                        }

                        toast({
                            title: "Búsqueda inicial lista",
                            description: `Se encontraron ${ppData.results.length} actuaciones. Ya puedes vigilar este proceso.`,
                        });
                    } else {
                        setPpResults([]);
                        toast({
                            title: "Búsqueda inicial completada",
                            description: "No se encontraron publicaciones previas inmediatas. El proceso está listo para ser vigilado.",
                        });
                    }
                } catch (err) {
                    toast({
                        title: "Consulta completada",
                        description: "Puedes proceder a vigilar el proceso para sincronizarlo automáticamente.",
                    });
                    setPpResults([]);
                } finally {
                    setIsLoadingActuaciones(false);
                }
            } else {
                const params: SearchParams = {
                    query,
                    type: selectedSources.includes("samai") && selectedSources.length === 1 && samaiSearchMode === "radicado" ? "radicado" : searchType,
                    sources: selectedSources,
                    tipoSujeto,
                    tipoPersona,
                    city: cityName === "all" ? "" : cityName,
                    specialty: specialty === "all" ? "" : specialty,
                    samai_search_mode: selectedSources.includes("samai") ? samaiSearchMode : undefined,
                    samai_exact_phrase: selectedSources.includes("samai") ? samaiExactPhrase : undefined,
                    samai_area_mode: selectedSources.includes("samai") ? samaiAreaMode : undefined,
                    samai_estado: selectedSources.includes("samai") ? samaiEstado : undefined,
                    samai_parte: selectedSources.includes("samai") ? samaiParte : undefined,
                    samai_corporacion: selectedSources.includes("samai") && samaiCorporacion !== "all" ? samaiCorporacion : undefined,
                };
                const data = await searchJudicial(params);
                setResults(data.results || []);
                setPpResults([]);
                setCpnuCard(null);
                setIsSearching(false);

                if (data.results && data.results.length > 0) {
                    const targetRad = data.results[0]?.radicado || query.trim();
                    if (targetRad && targetRad.length >= 10) {
                        setIsLoadingActuaciones(true);
                        fetchUltimasActuacionesPublicaciones({ radicado: targetRad })
                            .then((ppData) => {
                                if (ppData?.results?.length) {
                                    setPpResults(ppData.results);
                                }
                            })
                            .catch(() => {})
                            .finally(() => {
                                setIsLoadingActuaciones(false);
                            });
                    }
                }
            }
        } catch (err) {
            toast({ title: "Error de búsqueda", description: String(err), variant: "destructive" });
            setResults([]);
            setPpResults([]);
            setCpnuCard(null);
            setIsSearching(false);
        }
    };

    // Vigilar from publicaciones tab — adds proceso to vigilancia
    const handleVigilarFromPublicaciones = async (res: JudicialSearchResult) => {
        if (!user?.id) {
            toast({ title: "Inicio de sesión requerido", description: "Debes iniciar sesión con tu cuenta para vigilar procesos.", variant: "destructive" });
            return;
        }
        const usageCheck = await checkFeatureUsage(SEGUIMIENTO_FEATURE, DESPACHOS_DEFAULT_LIMITS.seguimientos);
        if (!usageCheck.allowed) {
            toast({ title: "Sin seguimientos disponibles", description: "Alcanzaste tu limite diario.", variant: "destructive" });
            return;
        }
        setWatchingIds((prev) => new Set(prev).add(res.radicado));
        toast({ title: "Iniciando vigilancia", description: `Configurando el monitoreo para el radicado ${res.radicado}...` });
        try {
            const fullMetadata = {
                ...(res || {}),
                ...(res?.metadata || {}),
                depto_id: hSelected.depto,
                muni_id: hSelected.muni,
                entidad_id: hSelected.entidad,
                esp_id: hSelected.esp,
                despacho_id: hSelected.despacho,
                historical: isHistorical,
            };

            const fuente = res?.fuente || "CPNU";
            const watchRes = await watchProceso(res.radicado, fuente, fullMetadata, user?.id);
            toast({
                title: "Expediente vigilado",
                description: watchRes.message || `Radicado ${res.radicado} añadido a Vigilancia Judicial. Redirigiendo...`
            });

            // Navigate the user to the procesos page so they can see it in the Vigilancia Judicial tab
            navigate("/dashboard/despachos/procesos", { state: { tab: "vigilancia" } });
        } catch (err) {
            toast({ title: "Error", description: String(err), variant: "destructive" });
        } finally {
            setWatchingIds((prev) => { const n = new Set(prev); n.delete(res.radicado); return n; });
        }
    };

    const handleWatchOffice = async () => {
        if (!hSelected.despacho) return;
        const usageCheck = await checkFeatureUsage(SEGUIMIENTO_FEATURE, DESPACHOS_DEFAULT_LIMITS.seguimientos);
        if (!usageCheck.allowed) {
            toast({
                title: "Sin seguimientos disponibles",
                description: "Alcanzaste tu limite diario de seguimientos. Vuelve a intentar manana.",
                variant: "destructive",
            });
            return;
        }
        setIsWatchingOffice(true);
        try {
            const officeName = hFilters.offices.find(o => o.id === hSelected.despacho)?.name || "Despacho seleccionado";
            await watchPublicacionesOffice({
                depto_id: hSelected.depto,
                muni_id: hSelected.muni,
                entidad_id: hSelected.entidad,
                esp_id: hSelected.esp,
                despacho_id: hSelected.despacho,
                office_name: officeName,
                historical: isHistorical,
            });
            toast({ title: "Monitoreo activado", description: `Estamos vigilando las publicaciones de ${officeName}.` });
        } catch (err) {
            toast({ title: "Error", description: String(err), variant: "destructive" });
        } finally {
            setIsWatchingOffice(false);
        }
    };

    const handleWatch = async (res: JudicialSearchResult, index: number) => {
        if (!user?.id) {
            toast({ title: "Inicio de sesión requerido", description: "Debes iniciar sesión con tu cuenta para vigilar procesos.", variant: "destructive" });
            return;
        }
        const resId = `${res.radicado}-${index}`;
        const usageCheck = await checkFeatureUsage(SEGUIMIENTO_FEATURE, DESPACHOS_DEFAULT_LIMITS.seguimientos);
        if (!usageCheck.allowed) {
            toast({
                title: "Sin seguimientos disponibles",
                description: "Alcanzaste tu limite diario de seguimientos. Vuelve a intentar manana.",
                variant: "destructive",
            });
            return;
        }
        setWatchingIds((prev) => new Set(prev).add(resId));
        toast({ title: "Iniciando vigilancia", description: `Añadiendo radicado ${res.radicado} a seguimiento...` });
        try {
            const watchRes = await watchProceso(res.radicado, res.fuente || "SAMAI", res, user?.id);
            toast({
                title: "Expediente vigilado",
                description: watchRes.message || `Radicado ${res.radicado} añadido y sincronización iniciada. Redirigiendo a Vigilancia Judicial...`
            });
            // Redirect to Vigilancia Judicial tab so the user can track extraction in real-time
            navigate("/dashboard/despachos/procesos", { state: { tab: "vigilancia" } });
        } catch (err) {
            toast({ title: "Error", description: String(err), variant: "destructive" });
        } finally {
            setWatchingIds((prev) => { const n = new Set(prev); n.delete(resId); return n; });
        }
    };

    const handleOpenProxyFile = async (fileUrl: string) => {
        try {
            const signedUrl = await getSignedPublicacionesProxyUrl(fileUrl);
            window.open(signedUrl, "_blank", "noopener,noreferrer");
        } catch (err) {
            toast({ title: "Error abriendo archivo", description: String(err), variant: "destructive" });
        }
    };

    return (
        <div className="animate-fade-in-up max-w-6xl mx-auto">
            <PageHeader
                title={samaiOnly ? "Búsqueda Judicial Multi-Fuentes" : "Buscador Judicial Multi-Fuente"}
                description={samaiOnly ? "Consulte procesos y estados en la jurisdicción contencioso administrativa" : "Busca procesos y publicaciones de la Rama Judicial en tiempo real"}
                icon={Globe}
                action={
                    !samaiOnly ? (
                        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                            <button
                                onClick={() => setActiveTab("search")}
                                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === "search" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:bg-gray-200"}`}
                            >
                                Procesos Judiciales
                            </button>
                            <button
                                onClick={() => setActiveTab("publicaciones")}
                                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${activeTab === "publicaciones" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500 hover:bg-gray-200"}`}
                            >
                                Publicaciones Procesales
                            </button>
                        </div>
                    ) : undefined
                }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <Card className="border border-indigo-100 bg-indigo-50/40">
                    <CardContent className="p-4">
                        <p className="text-[10px] font-bold uppercase text-indigo-500">Consultas (hoy)</p>
                        <div className="flex items-end justify-between mt-1">
                            <p className="text-xl font-black text-indigo-700">{consultaRemaining}</p>
                            <p className="text-xs text-indigo-500">restantes de {consultaLimit}</p>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1">Usadas: {consultaUsed}</p>
                    </CardContent>
                </Card>
                <Card className="border border-emerald-100 bg-emerald-50/40">
                    <CardContent className="p-4">
                        <p className="text-[10px] font-bold uppercase text-emerald-600">Seguimientos (hoy)</p>
                        <div className="flex items-end justify-between mt-1">
                            <p className="text-xl font-black text-emerald-700">{seguimientoRemaining}</p>
                            <p className="text-xs text-emerald-600">restantes de {seguimientoLimit}</p>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1">Usados: {seguimientoUsed}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Search Form */}
            <Card className="border border-gray-100/80 shadow-lg mb-8 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-b border-gray-100 px-6 py-4">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Search className="h-3.5 w-3.5" /> Parámetros de Búsqueda
                    </h3>
                </div>
                <CardContent className="p-6">
                    <form onSubmit={handleSearch} className="space-y-5">
                        {samaiOnly && (
                            <div className="flex items-center gap-3 bg-gray-50/50 p-3 rounded-xl border border-gray-100 mb-2">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Fuentes:</span>
                                {SOURCES.filter(s => ["samai", "cpnu", "tyba"].includes(s.id)).map((src) => (
                                    <button
                                        key={src.id}
                                        type="button"
                                        onClick={() => toggleSource(src.id)}
                                        className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${selectedSources.includes(src.id)
                                            ? src.color
                                            : "bg-white text-gray-300 border-gray-200"
                                            }`}
                                    >
                                        {src.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {activeTab === "search" ? (
                            <>
                                {searchType === "radicado" && (
                                    <div className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl p-2.5 flex items-center gap-2 mb-3">
                                        <Info className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                                        <span>En <strong>CPNU</strong> y <strong>SAMAI</strong>, la búsqueda por <strong>Número de Radicado (23 dígitos)</strong> realiza una consulta nacional directa e instantánea sin requerir ciudad ni especialidad.</span>
                                    </div>
                                )}

                                {/* Row 1: Type + Query + City */}
                                <div className="flex gap-3">
                                    <Select value={searchType} onValueChange={(v) => setSearchType(v as SearchParams["type"])}>
                                        <SelectTrigger className="w-56 rounded-xl font-medium">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="nombre">Nombre / Razón Social</SelectItem>
                                            <SelectItem value="radicado">Número de Radicado</SelectItem>
                                            <SelectItem value="juez">Juez / Magistrado</SelectItem>
                                            <SelectItem value="juzgado">Juzgado / Clase Proceso</SelectItem>
                                        </SelectContent>
                                    </Select>

                                    <Input
                                        className="flex-1 rounded-xl"
                                        placeholder={searchType === "radicado" ? "Ingrese el radicado de 23 dígitos (ej: 05001310500120230001000)..." : `Buscar por ${searchType}...`}
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                    />

                                    {selectedSources.includes("samai") && (
                                        <Select value={samaiCorporacion} onValueChange={setSamaiCorporacion}>
                                            <SelectTrigger className="w-60 rounded-xl">
                                                <Landmark className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                                <SelectValue placeholder="Corporación o juzgado" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todas las corporaciones</SelectItem>
                                                {(samaiFilters.corporacion_options || [])
                                                    .filter((o: any) => o.value && String(o.value).trim() !== "")
                                                    .map((o: any) => (
                                                        <SelectItem key={`${o.value}-${o.label || o.text}`} value={String(o.value)}>{o.label || o.text}</SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    <Select value={cityName} onValueChange={setCityName}>
                                        <SelectTrigger className="w-52 rounded-xl">
                                            <MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                            <SelectValue placeholder="Todas las ciudades" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todas las ciudades</SelectItem>
                                            {cities.map((c) => (
                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    {cityName && specialties.length > 0 && (
                                        <Select value={specialty} onValueChange={setSpecialty}>
                                            <SelectTrigger className="w-60 rounded-xl">
                                                <Scale className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                                <SelectValue placeholder="Todas las especialidades" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">Todas las especialidades</SelectItem>
                                                {specialties.map((s) => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}

                                    <Button
                                        type="submit"
                                        disabled={isSearching || !query.trim() || selectedSources.length === 0}
                                        className="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md shadow-indigo-200 px-8"
                                    >
                                        {isSearching ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Buscando...</>
                                        ) : (
                                            <><Search className="h-4 w-4 mr-2" /> Buscar</>
                                        )}
                                    </Button>
                                </div>

                                {/* Row 2: Sources + Tipo Sujeto/Persona */}
                                {(!samaiOnly || searchType === "nombre") && (
                                    <div className="flex items-center gap-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                        {!samaiOnly && (
                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Fuentes:</span>
                                                {SOURCES.map((src) => (
                                                    <button
                                                        key={src.id}
                                                        type="button"
                                                        onClick={() => toggleSource(src.id)}
                                                        className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all ${selectedSources.includes(src.id)
                                                            ? src.color
                                                            : "bg-white text-gray-300 border-gray-200"
                                                            }`}
                                                    >
                                                        {src.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {searchType === "nombre" && (
                                            <div className={`flex items-center gap-4 ${!samaiOnly ? 'border-l border-gray-200 pl-6' : ''}`}>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Tipo Sujeto</span>
                                                    <select
                                                        className="text-xs font-bold text-gray-600 bg-transparent outline-none cursor-pointer"
                                                        value={tipoSujeto}
                                                        onChange={(e) => setTipoSujeto(e.target.value)}
                                                    >
                                                        <option value="0001">Demandante</option>
                                                        <option value="0002">Demandado</option>
                                                    </select>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">Tipo Persona</span>
                                                    <select
                                                        className="text-xs font-bold text-gray-600 bg-transparent outline-none cursor-pointer"
                                                        value={tipoPersona}
                                                        onChange={(e) => setTipoPersona(e.target.value)}
                                                    >
                                                        <option value="1">Natural</option>
                                                        <option value="2">Jurídica</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="col-span-1 space-y-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">1. Departamento</label>
                                        <Select value={hSelected.depto} onValueChange={(v) => setHSelected(p => ({ ...p, depto: v }))}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                            <SelectContent>{hFilters.deptos.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.internal_id || d.id.replace('department_', '')})</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">2. Municipio</label>
                                        <Select disabled={!hSelected.depto} value={hSelected.muni} onValueChange={(v) => setHSelected(p => ({ ...p, muni: v }))}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                            <SelectContent>{hFilters.munis.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.internal_id || d.id.replace('municipality_', '')})</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">3. Corporación / Entidad</label>
                                        <Select value={hSelected.entidad} onValueChange={(v) => setHSelected(p => ({ ...p, entidad: v }))}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                            <SelectContent>{hFilters.entities.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.internal_id || d.id.replace('entity_', '')})</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-span-1 space-y-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">4. Especialidad</label>
                                        <Select disabled={!(hSelected.muni && hSelected.entidad)} value={hSelected.esp} onValueChange={(v) => setHSelected(p => ({ ...p, esp: v }))}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                            <SelectContent>{hFilters.specialties.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.internal_id || d.id.replace('specialty_', '')})</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">5. Despacho Judicial</label>
                                        <Select disabled={!(hSelected.muni && hSelected.entidad && hSelected.esp)} value={hSelected.despacho} onValueChange={(v) => setHSelected(p => ({ ...p, despacho: v }))}>
                                            <SelectTrigger className="rounded-xl"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                            <SelectContent>{hFilters.offices.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.internal_id || d.id.replace('office_', '')})</SelectItem>)}</SelectContent>
                                        </Select>
                                        {isLoadingOffices && (
                                            <p className="text-[11px] text-indigo-500">Cargando despachos...</p>
                                        )}
                                        {!isLoadingOffices && officeLoadMessage && (
                                            <p className="text-[11px] text-amber-600">{officeLoadMessage}</p>
                                        )}
                                        {!isLoadingOffices && !officeLoadMessage && hFilters.offices.length > 0 && (
                                            <p className="text-[11px] text-emerald-600">{hFilters.offices.length} despachos encontrados</p>
                                        )}
                                        <div className="flex flex-col gap-1.5 pt-1">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">6. Número de Radicado (23 dígitos)</label>
                                            <Input
                                                className="rounded-xl"
                                                placeholder="Ingrese el radicado de 23 dígitos..."
                                                value={radicadoFilter}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/-/g, "").replace(/\s/g, "").replace(/%20/g, "");
                                                    setRadicadoFilter(val);
                                                }}
                                                maxLength={30}
                                            />
                                            {radicadoFilter.length > 0 && radicadoFilter.length < 23 && (
                                                <p className="text-[11px] text-amber-600 mt-1">{radicadoFilter.length}/23 dígitos</p>
                                            )}
                                            <p className="text-[11px] font-medium text-indigo-600 mt-1">
                                                💡 Al ingresar el radicado de 23 dígitos, la búsqueda se habilita directamente a nivel nacional.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="pt-2 flex items-center space-x-2">
                                        <Switch
                                            id="historical-search"
                                            checked={isHistorical}
                                            onCheckedChange={setIsHistorical}
                                        />
                                        <Label htmlFor="historical-search" className="text-sm font-medium text-gray-700 cursor-pointer">
                                            Búsqueda histórica (Antes del 14 de mayo 2024)
                                        </Label>
                                    </div>
                                    <div className="pt-5 flex gap-2">
                                        <Button
                                            type="submit"
                                            disabled={isSearching || (radicadoFilter.trim().length < 10 && !hSelected.despacho)}
                                            className="flex-1 rounded-xl bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200"
                                        >
                                            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" /> Buscar Publicaciones</>}
                                        </Button>
                                        {/* {hSelected.despacho && (
                                            <Button
                                                type="button"
                                                onClick={handleWatchOffice}
                                                disabled={isWatchingOffice}
                                                variant="outline"
                                                className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                                            >
                                                {isWatchingOffice ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bookmark className="h-4 w-4 mr-2" /> Vigilar Despacho</>}
                                            </Button>
                                        )} */}
                                    </div>
                                </div>
                                <div className="col-span-1 hidden md:flex items-center justify-center p-6 bg-indigo-50/50 rounded-2xl border border-indigo-100/50">
                                    <div className="text-center">
                                        <Scale className="h-10 w-10 text-indigo-400 mx-auto mb-2 opacity-50" />
                                        <p className="text-xs font-medium text-indigo-600/70 leading-relaxed italic">
                                            "Seleccione el despacho para consultar sus estados y traslados recientes"
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </CardContent>
            </Card>

            {/* Results */}
            {isSearching && (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-gray-100 border-t-indigo-600 rounded-full animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Database className="h-5 w-5 text-indigo-400" />
                        </div>
                    </div>
                    <p className="mt-4 text-gray-400 text-sm animate-pulse font-medium">Orquestando búsqueda en fuentes judiciales...</p>
                </div>
            )}

            {!isSearching && activeTab === "search" && results.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-gray-400 font-medium">
                            Se encontraron <span className="text-indigo-600 font-bold">{results.length}</span> resultados
                        </p>
                    </div>
                    {results.map((res, i) => (
                        <Card
                            key={`${res.radicado}-${i}`}
                            className="border border-gray-100/80 hover:shadow-lg hover:border-indigo-100 transition-all duration-300 group overflow-hidden"
                        >
                            {/* Header bar */}
                            <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Badge className={SOURCES.find((s) => s.id === res.fuente?.toLowerCase())?.color || "bg-gray-100 text-gray-600"}>
                                        {String(res.fuente || "")}
                                    </Badge>
                                    <span className="font-mono text-sm font-bold text-indigo-600 tracking-wide">{String(res.radicado || "")}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    {(() => {
                                        const statusInfo = getVigenteStatusInfo(res.estado);
                                        return (
                                            <Badge className={statusInfo.badgeClass}>
                                                Vigente: {statusInfo.text}
                                            </Badge>
                                        );
                                    })()}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={watchingIds.has(`${res.radicado}-${i}`)}
                                        onClick={() => handleWatch(res, i)}
                                        className="rounded-xl border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300"
                                    >
                                        {watchingIds.has(`${res.radicado}-${i}`) ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <><Bookmark className="h-4 w-4 mr-1.5" /> Vigilar</>
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <CardContent className="p-5">
                                {/* Scrapper status message banner */}
                                {isLoadingActuaciones ? (
                                    <div className="bg-amber-50/90 border border-amber-200/80 rounded-xl p-4 mb-5 flex items-start gap-3.5 shadow-xs">
                                        <div className="p-2 bg-amber-100/80 rounded-lg shrink-0 mt-0.5">
                                            <Loader2 className="h-4 w-4 text-amber-700 animate-spin" />
                                        </div>
                                        <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                                            <div className="flex items-center justify-between font-bold text-amber-950 text-sm mb-1">
                                                <span>Búsqueda inicial de actuaciones en curso...</span>
                                                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                                    Consultando portales
                                                </Badge>
                                            </div>
                                            <p>
                                                El scrapper está consultando actuaciones y publicaciones en tiempo real. Este proceso puede tomar entre 30 segundos y 2 minutos.
                                            </p>
                                            <div className="mt-2 font-medium text-amber-950 flex items-center gap-1.5 bg-amber-100/60 p-2.5 rounded-lg border border-amber-200/70">
                                                <Bookmark className="h-4 w-4 text-indigo-600 shrink-0" />
                                                <span><strong>No es necesario esperar:</strong> Puedes presionar el botón <strong>"Vigilar"</strong> a continuación para iniciar el monitoreo inmediato.</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-emerald-50/80 border border-emerald-200/80 rounded-xl p-3.5 mb-5 flex items-center gap-2.5 text-xs text-emerald-900 shadow-xs">
                                        <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-700 shrink-0">
                                            <CheckCircle2 className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <span className="font-bold text-emerald-950 text-sm block">Búsqueda inicial completada</span>
                                            <span className="text-emerald-800 text-[11px]">
                                                {ppResults.length > 0
                                                    ? `Se encontraron ${ppResults.length} actuaciones/publicaciones del proceso.`
                                                    : "Extracción inicial finalizada. Ya puedes activar la vigilancia automática de este proceso."}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Title & Subtitle */}
                                <div className="mb-6">
                                    <h4 className="font-bold text-xl text-gray-900 leading-tight">
                                        {res.clase || res.marco_legal || "PROCESO JUDICIAL"}{" "}
                                        {res.tipo_proceso && (
                                            <span className="font-normal text-indigo-700 uppercase">
                                                — {typeof res.tipo_proceso === 'object' ? JSON.stringify(res.tipo_proceso) : String(res.tipo_proceso || "")}
                                            </span>
                                        )}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1.5 flex flex-wrap gap-2 items-center">
                                        <span>Fecha de Radicación:</span>
                                        <span className="font-semibold text-gray-700">{res.fecha_radicado || res.fecha_demanda || "FECHA NO REGISTRADA"}</span>
                                        {res.subclase && res.subclase !== "SIN SUBCLASE DE PROCESO" && (
                                            <>
                                                <span className="text-gray-300">•</span>
                                                <span>Subclase:</span>
                                                <span className="font-semibold text-gray-700 uppercase">{res.subclase}</span>
                                            </>
                                        )}
                                    </p>
                                </div>

                                {/* Metadata Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 text-xs mb-6 bg-gray-50/60 p-4 rounded-xl border border-gray-100/80">
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">FECHA DE RADICACIÓN:</span>
                                        <span className="font-bold text-gray-800 block">
                                            {res.fecha_radicado || res.fecha_demanda || "FECHA NO REGISTRADA"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">DESPACHO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {typeof res.juzgado === 'object' ? JSON.stringify(res.juzgado) : String(res.juzgado || res.ubicacion || "DESPACHO NO REGISTRADO")}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">PONENTE:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {res.ponente || "SIN PONENTE ASIGNADO"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">TIPO DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {typeof res.tipo_proceso === 'object' ? JSON.stringify(res.tipo_proceso) : String(res.tipo_proceso || "SIN TIPO DE PROCESO")}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">CLASE DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {res.clase || res.marco_legal || "SIN CLASE DE PROCESO"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">SUBCLASE DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {res.subclase || "SIN SUBCLASE DE PROCESO"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">RECURSO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {res.recurso || "SIN TIPO DE RECURSO"}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">UBICACIÓN DEL EXPEDIENTE:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {res.ubicacion || res.ubicacion_expediente || (typeof res.juzgado === 'object' ? JSON.stringify(res.juzgado) : String(res.juzgado || "DESPACHO CONOCIMIENTO"))}
                                        </span>
                                    </div>
                                    {res.contenido_radicacion && (
                                        <div className="md:col-span-2 lg:col-span-3">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">CONTENIDO DE RADICACIÓN:</span>
                                            <span className="font-semibold text-gray-800 uppercase leading-relaxed block bg-white p-3 rounded-lg border border-gray-200/80 font-mono text-[11px] tracking-tight">
                                                {res.contenido_radicacion}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Sujetos Procesales */}
                                {(() => {
                                    const rawSujetos = res.sujetos as any;
                                    let list: any[] = [];
                                    if (Array.isArray(rawSujetos)) {
                                        list = rawSujetos;
                                    } else if (typeof rawSujetos === 'string' && rawSujetos.trim()) {
                                        list = rawSujetos.split(/\n|\|/).map(s => s.trim()).filter(Boolean);
                                    }
                                    if (list.length === 0) return null;

                                    return (
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 shadow-sm">
                                            <div className="bg-[#15803d] text-white px-4 py-3">
                                                <h4 className="text-sm font-bold m-0 tracking-wide">Sujetos Procesales</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left text-xs text-gray-700 border-collapse">
                                                    <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] font-bold border-b border-gray-200">
                                                        <tr>
                                                            <th className="px-4 py-2.5 text-center w-16 border-r border-gray-200">REG</th>
                                                            <th className="px-4 py-2.5 w-1/4 border-r border-gray-200">TIPO DE SUJETO</th>
                                                            <th className="px-4 py-2.5">NOMBRE / DESCRIPCIÓN DEL SUJETO</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {list.map((s: any, si) => (
                                                            <tr key={si} className="hover:bg-gray-50/50 transition-colors">
                                                                <td className="px-4 py-2.5 text-center text-gray-400 border-r border-gray-100 font-medium">{si + 1}</td>
                                                                <td className="px-4 py-2.5 font-semibold text-gray-600 border-r border-gray-100 uppercase">{typeof s === 'string' ? (s.includes(':') ? s.split(':')[0] : 'SUJETO') : s.tipo}</td>
                                                                <td className="px-4 py-2.5 text-gray-700 uppercase">{typeof s === 'string' ? (s.includes(':') ? s.split(':').slice(1).join(':') : s) : s.nombre}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Asunto */}
                                {res.asunto && (
                                    <div className="bg-[#fffbeb] p-4 rounded-xl border border-amber-200/60 shadow-sm">
                                        <span className="text-[10px] font-extrabold text-[#b45309] uppercase block mb-1.5 tracking-wider">
                                            ASUNTO
                                        </span>
                                        <p className="text-xs text-gray-700 leading-relaxed font-semibold uppercase">
                                            {typeof res.asunto === 'object' ? JSON.stringify(res.asunto) : String(res.asunto || "")}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* CPNU Process Card — shown in publicaciones tab after radicado search */}
            {!isSearching && activeTab === "publicaciones" && cpnuCard && (() => {
                const meta = cpnuCard.metadata || {};
                const fechaRadicacion = cpnuCard.fecha_radicado || meta["Fecha de Radicación"] || meta["fecha_radicacion"] || "FECHA NO REGISTRADA";
                const despacho = typeof cpnuCard.juzgado === 'object' ? JSON.stringify(cpnuCard.juzgado) : String(cpnuCard.juzgado || meta["Despacho"] || meta["Despacho Actual"] || "DESPACHO NO DISPONIBLE");
                const ponente = (cpnuCard.ponente && cpnuCard.ponente !== "N/A" && cpnuCard.ponente !== "SIN PONENTE") ? cpnuCard.ponente : (meta["Ponente"] || meta["Juez / Ponente"] || "SIN PONENTE");
                const tipoProceso = cpnuCard.tipo_proceso || meta["Tipo de Proceso"] || meta["Tipo"] || cpnuCard.marco_legal || "PROCESO JUDICIAL";
                const claseProceso = cpnuCard.clase || meta["Clase de Proceso"] || meta["Clase"] || "SIN CLASE DE PROCESO";
                const subclaseProceso = cpnuCard.subclase || meta["Subclase de Proceso"] || meta["Subclase"] || "SIN SUBCLASE DE PROCESO";
                const recurso = cpnuCard.recurso || meta["Recurso"] || "SIN TIPO DE RECURSO";
                const ubicacionExpediente = cpnuCard.ubicacion_expediente || meta["Ubicación del Expediente"] || meta["Ubicación"] || meta["Ubicacion"] || "DESPACHO CONOCIMIENTO";
                const contenidoRadicacion = cpnuCard.contenido_radicacion || meta["Contenido de Radicación"] || meta["Contenido"] || null;

                return (
                    <div className="space-y-3 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-gray-400 font-medium">
                                Se encontraron <span className="text-indigo-600 font-bold">1</span> resultados
                            </p>
                        </div>
                        <Card className="border border-gray-100/80 hover:shadow-lg hover:border-indigo-100 transition-all duration-300 group overflow-hidden">
                            <CardContent className="p-6">
                                {/* Scrapper status message banner */}
                                {isLoadingActuaciones ? (
                                    <div className="bg-amber-50/90 border border-amber-200/80 rounded-xl p-4 mb-5 flex items-start gap-3.5 shadow-xs">
                                        <div className="p-2 bg-amber-100/80 rounded-lg shrink-0 mt-0.5">
                                            <Loader2 className="h-4 w-4 text-amber-700 animate-spin" />
                                        </div>
                                        <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                                            <div className="flex items-center justify-between font-bold text-amber-950 text-sm mb-1">
                                                <span>Búsqueda inicial de actuaciones en curso...</span>
                                                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                                    Consultando portales
                                                </Badge>
                                            </div>
                                            <p>
                                                El scrapper está consultando actuaciones y publicaciones en tiempo real. Este proceso puede tomar entre 30 segundos y 2 minutos.
                                            </p>
                                            <div className="mt-2 font-medium text-amber-950 flex items-center gap-1.5 bg-amber-100/60 p-2.5 rounded-lg border border-amber-200/70">
                                                <Bookmark className="h-4 w-4 text-indigo-600 shrink-0" />
                                                <span><strong>No es necesario esperar:</strong> Puedes presionar el botón <strong>"Vigilar"</strong> a continuación para iniciar el monitoreo inmediato.</span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-emerald-50/80 border border-emerald-200/80 rounded-xl p-3.5 mb-5 flex items-center gap-2.5 text-xs text-emerald-900 shadow-xs">
                                        <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-700 shrink-0">
                                            <CheckCircle2 className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <span className="font-bold text-emerald-950 text-sm block">Búsqueda inicial completada</span>
                                            <span className="text-emerald-800 text-[11px]">
                                                {ppResults.length > 0
                                                    ? `Se encontraron ${ppResults.length} actuaciones/publicaciones del proceso.`
                                                    : "Extracción inicial finalizada. Ya puedes activar la vigilancia automática de este proceso."}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {/* Card Header Row */}
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-100 pb-5 mb-5">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <Badge className={`${cpnuCard.fuente?.toLowerCase() === 'samai'
                                            ? 'bg-[#fef08a] text-[#854d0e] border-[#fef9c3]'
                                            : SOURCES.find((s) => s.id === cpnuCard.fuente?.toLowerCase())?.color || "bg-gray-100 text-gray-700"
                                            } hover:bg-opacity-80 font-bold px-3 py-1 rounded-full text-xs tracking-wider`}>
                                            {String(cpnuCard.fuente || "SAMAI").toUpperCase()}
                                        </Badge>
                                        <span className="font-mono text-base font-bold text-indigo-700 tracking-wide">
                                            {cpnuCard.radicado}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {(() => {
                                            const statusInfo = getVigenteStatusInfo(cpnuCard.estado);
                                            return (
                                                <Badge className={`${statusInfo.badgeClass} font-bold px-3 py-1 rounded-full text-xs`}>
                                                    Vigente: {statusInfo.text}
                                                </Badge>
                                            );
                                        })()}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={watchingIds.has(cpnuCard.radicado)}
                                            onClick={() => handleVigilarFromPublicaciones(cpnuCard)}
                                            className="rounded-full border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300 font-semibold px-4 py-1.5 h-auto text-xs flex items-center gap-1.5 transition-all duration-200 shadow-sm"
                                        >
                                            {watchingIds.has(cpnuCard.radicado) ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <><Bookmark className="h-3.5 w-3.5" /> Vigilar</>
                                            )}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setCpnuCard(null);
                                                setPpResults([]);
                                                toast({ title: "Búsqueda descartada", description: "Puedes ajustar los filtros o el radicado e intentar de nuevo." });
                                            }}
                                            className="rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 font-medium px-3 py-1.5 h-auto text-xs flex items-center gap-1"
                                            title="Si este no es el proceso que buscas, haz clic para limpiar y reintentar"
                                        >
                                            <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                            Buscar otro
                                        </Button>
                                    </div>
                                </div>

                                {/* Title & Subtitle */}
                                <div className="mb-6">
                                    <h4 className="font-bold text-xl text-gray-900 leading-tight">
                                        {tipoProceso}{" "}
                                        {claseProceso && claseProceso !== "SIN CLASE DE PROCESO" && (
                                            <span className="font-normal text-indigo-700 uppercase">
                                                — {claseProceso}
                                            </span>
                                        )}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1.5 flex flex-wrap gap-2 items-center">
                                        <span>Fecha de Radicación:</span>
                                        <span className="font-semibold text-gray-700">{fechaRadicacion}</span>
                                        {subclaseProceso && subclaseProceso !== "SIN SUBCLASE DE PROCESO" && (
                                            <>
                                                <span className="text-gray-300">•</span>
                                                <span>Subclase:</span>
                                                <span className="font-semibold text-gray-700 uppercase">{subclaseProceso}</span>
                                            </>
                                        )}
                                    </p>
                                </div>

                                {/* Metadata Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 text-xs mb-6 bg-gray-50/60 p-4 rounded-xl border border-gray-100/80">
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">FECHA DE RADICACIÓN:</span>
                                        <span className="font-bold text-gray-800 block">
                                            {fechaRadicacion}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">DESPACHO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {despacho}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">PONENTE:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {ponente}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">TIPO DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {tipoProceso}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">CLASE DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {claseProceso}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">SUBCLASE DE PROCESO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {subclaseProceso}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">RECURSO:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {recurso}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">UBICACIÓN DEL EXPEDIENTE:</span>
                                        <span className="font-bold text-gray-800 uppercase leading-snug block">
                                            {ubicacionExpediente}
                                        </span>
                                    </div>
                                    {contenidoRadicacion && (
                                        <div className="md:col-span-2 lg:col-span-3">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">CONTENIDO DE RADICACIÓN:</span>
                                            <span className="font-semibold text-gray-800 uppercase leading-relaxed block bg-white p-3 rounded-lg border border-gray-200/80 font-mono text-[11px] tracking-tight">
                                                {contenidoRadicacion}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Sujetos Procesales */}
                                {(() => {
                                    const rawSujetos = cpnuCard.sujetos as any;
                                    let list: any[] = [];
                                    if (Array.isArray(rawSujetos)) {
                                        list = rawSujetos;
                                    } else if (typeof rawSujetos === 'string' && rawSujetos.trim()) {
                                        list = rawSujetos.split(/\n|\|/).map(s => s.trim()).filter(Boolean);
                                    }
                                    if (list.length === 0) return null;

                                    return (
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 shadow-sm">
                                            <div className="bg-[#15803d] text-white px-4 py-3">
                                                <h4 className="text-sm font-bold m-0 tracking-wide">Sujetos Procesales</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left text-xs text-gray-700 border-collapse">
                                                    <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] font-bold border-b border-gray-200">
                                                        <tr>
                                                            <th className="px-4 py-2.5 text-center w-16 border-r border-gray-200">REG</th>
                                                            <th className="px-4 py-2.5 w-1/4 border-r border-gray-200">TIPO DE SUJETO</th>
                                                            <th className="px-4 py-2.5">NOMBRE / DESCRIPCIÓN DEL SUJETO</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {list.map((s: any, si) => (
                                                            <tr key={si} className="hover:bg-gray-50/50 transition-colors">
                                                                <td className="px-4 py-2.5 text-center text-gray-400 border-r border-gray-100 font-medium">{si + 1}</td>
                                                                <td className="px-4 py-2.5 font-semibold text-gray-600 border-r border-gray-100 uppercase">{typeof s === 'string' ? (s.includes(':') ? s.split(':')[0] : 'SUJETO') : (s.tipo || 'SUJETO')}</td>
                                                                <td className="px-4 py-2.5 text-gray-700 uppercase">{typeof s === 'string' ? (s.includes(':') ? s.split(':').slice(1).join(':') : s) : (s.nombre || JSON.stringify(s))}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Asunto */}
                                {cpnuCard.asunto && (
                                    <div className="bg-[#fffbeb] p-4 rounded-xl border border-amber-200/60 shadow-sm">
                                        <span className="text-[10px] font-extrabold text-[#b45309] uppercase block mb-1.5 tracking-wider">
                                            ASUNTO
                                        </span>
                                        <p className="text-xs text-gray-700 leading-relaxed font-semibold uppercase">
                                            {typeof cpnuCard.asunto === 'object' ? JSON.stringify(cpnuCard.asunto) : String(cpnuCard.asunto || "")}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                );
            })()}

            {/* Loading actuaciones after clicking Vigilar */}
            {isLoadingActuaciones && (
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="relative">
                        <div className="w-12 h-12 border-4 border-gray-100 border-t-emerald-600 rounded-full animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Database className="h-4 w-4 text-emerald-400" />
                        </div>
                    </div>
                    <p className="mt-4 text-gray-500 text-sm animate-pulse font-medium text-center">
                        El scrapper inicial está extrayendo actuaciones y documentos del proceso en tiempo real...<br />
                        <span className="text-xs text-indigo-600 font-semibold">Puedes hacer clic en "Vigilar" en la tarjeta superior en cualquier momento.</span>
                    </p>
                </div>
            )}

            {!isSearching && !isLoadingActuaciones && activeTab === "publicaciones" && ppResults.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-gray-400 font-medium">
                            Se encontraron <span className="text-indigo-600 font-bold">{ppResults.length}</span> actuaciones/publicaciones
                            {(() => {
                                const cpnuCount = ppResults.filter(r => r.fuente === "CPNU").length;
                                const pubCount = ppResults.filter(r => r.fuente !== "CPNU").length;
                                return (cpnuCount > 0 && pubCount > 0) ? (
                                    <span className="ml-2 text-xs text-gray-400">
                                        ({cpnuCount} CPNU · {pubCount} {samaiOnly ? "SAMAI" : "Publicaciones"})
                                    </span>
                                ) : null;
                            })()}
                        </p>
                    </div>
                    {!samaiOnly && ppResults.map((res, i) => {
                        const isCPNU = res.fuente === "CPNU";
                        return (
                            <Card
                                key={`${res.radicado || "sin-radicado"}-${res.fuente}-${i}`}
                                className={`border hover:shadow-lg transition-all duration-300 group ${isCPNU
                                    ? "border-emerald-100/80 hover:border-emerald-200"
                                    : "border-gray-100/80 hover:border-indigo-100"
                                    }`}
                            >
                                <CardContent className="p-5">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Badge className={
                                                res.fuente?.toUpperCase() === 'CPNU'
                                                    ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                                                    : res.fuente?.toUpperCase() === 'TYBA'
                                                        ? 'bg-rose-100 text-rose-700 border-rose-200'
                                                        : res.fuente?.toUpperCase() === 'SAMAI'
                                                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                                                            : 'bg-indigo-100 text-indigo-700 border-indigo-200'
                                            }>
                                                {res.fuente || (samaiOnly ? "SAMAI" : "PUBLICACIONES")}
                                            </Badge>
                                            {res.radicado && <span className="font-mono text-sm font-bold text-indigo-600">{res.radicado}</span>}
                                        </div>
                                        <h4 className="font-bold text-gray-800">{typeof res.title === 'object' ? JSON.stringify(res.title) : String(res.title || "Actuación / Publicación")}</h4>
                                        <p className="text-xs text-gray-500">{res.publication_date || "Fecha no disponible"}</p>

                                        {/* CPNU metadata summary */}
                                        {isCPNU && res.metadata && Object.keys(res.metadata).length > 0 && (
                                            <div className="mt-2 bg-emerald-50/50 p-3 rounded-lg border border-emerald-100">
                                                <span className="text-[10px] font-bold text-emerald-600 uppercase block mb-1.5">Información del Proceso (CPNU)</span>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                                                    {res.metadata["Despacho"] && (
                                                        <p className="text-[11px] text-gray-600"><span className="font-semibold text-gray-700">Despacho:</span> {typeof res.metadata["Despacho"] === 'object' ? JSON.stringify(res.metadata["Despacho"]) : String(res.metadata["Despacho"] || "")}</p>
                                                    )}
                                                    {res.metadata["Ponente"] && (
                                                        <p className="text-[11px] text-gray-600"><span className="font-semibold text-gray-700">Ponente:</span> {typeof res.metadata["Ponente"] === 'object' ? JSON.stringify(res.metadata["Ponente"]) : String(res.metadata["Ponente"] || "")}</p>
                                                    )}
                                                    {res.metadata["Clase de Proceso"] && (
                                                        <p className="text-[11px] text-gray-600"><span className="font-semibold text-gray-700">Clase:</span> {typeof res.metadata["Clase de Proceso"] === 'object' ? JSON.stringify(res.metadata["Clase de Proceso"]) : String(res.metadata["Clase de Proceso"] || "")}</p>
                                                    )}
                                                    {res.metadata["Tipo de Proceso"] && (
                                                        <p className="text-[11px] text-gray-600"><span className="font-semibold text-gray-700">Tipo:</span> {typeof res.metadata["Tipo de Proceso"] === 'object' ? JSON.stringify(res.metadata["Tipo de Proceso"]) : String(res.metadata["Tipo de Proceso"] || "")}</p>
                                                    )}
                                                </div>
                                                {res.metadata["sujetos_completos"] && (
                                                    <div className="mt-2 pt-2 border-t border-emerald-100">
                                                        <span className="text-[10px] font-bold text-emerald-600 uppercase block mb-1">Sujetos Procesales</span>
                                                        <p className="text-[11px] text-gray-600 leading-tight whitespace-pre-wrap">{typeof res.metadata["sujetos_completos"] === 'object' ? JSON.stringify(res.metadata["sujetos_completos"]) : String(res.metadata["sujetos_completos"] || "")}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {res.detail && !isCPNU && (
                                            <div className="mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Detalle</span>
                                                <p className="text-[11px] text-gray-600 leading-tight">{res.detail}</p>
                                            </div>
                                        )}
                                        {res.detail && isCPNU && !res.metadata && (
                                            <div className="mt-2 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Anotación</span>
                                                <p className="text-[11px] text-gray-600 leading-tight">{res.detail}</p>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-2">
                                                {isCPNU ? "Documentos" : "Archivos"}
                                            </span>
                                            {res.files?.length ? (
                                                <div className="flex flex-wrap gap-2">
                                                    {res.files.map((f, idx) => (
                                                        <button
                                                            key={`${f.url}-${idx}`}
                                                            type="button"
                                                            onClick={() => {
                                                                if (isCPNU) {
                                                                    // CPNU files come from consultaprocesos.ramajudicial.gov.co
                                                                    // Open directly without the proxy
                                                                    window.open(f.url, "_blank", "noopener,noreferrer");
                                                                } else {
                                                                    handleOpenProxyFile(f.url);
                                                                }
                                                            }}
                                                            className={`text-xs px-2 py-1 rounded-lg border ${isCPNU
                                                                ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                                                                : "border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                                                                }`}
                                                        >
                                                            {f.text || "Abrir archivo"}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400">
                                                    {isCPNU ? "Sin documentos en esta actuación." : "Sin archivos visibles en esta actuación."}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {!isSearching && !isLoadingActuaciones && hasSearched && ((activeTab === "search" && results.length === 0) || (activeTab === "publicaciones" && ppResults.length === 0 && !cpnuCard)) && (
                <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                    <Search className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 text-lg font-medium">No se encontraron resultados.</p>
                    <p className="text-gray-300 text-sm mt-1">Intenta con otros términos o activa más fuentes.</p>
                </div>
            )}

            {!hasSearched && (
                <div className="text-center py-20">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                        <Scale className="h-10 w-10 text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-700">Explora la Rama Judicial</h3>
                    <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
                        Busca por nombre, radicado, juez o juzgado en múltiples fuentes de datos judiciales simultáneamente.
                    </p>
                </div>
            )}
        </div>
    );
};

export const SamaiJudicialPage = () => <JudicialSearchPage samaiOnly />;
